import { useEffect, useState } from "react";
import axios from "axios";
import { Col, Row, Spinner } from "react-bootstrap";
import { useMyPlayer } from "../../context/MyPlayerContext";
import type { GquestParticipant, GquestRow } from "../../types/gquests";
import PlayerLink from "../common/PlayerLink";
import "../player/pk/pk.css";

// Participants don't change once a GQ is stored, so keep them for the session.
const participantCache = new Map<number, GquestParticipant[]>();

const RESULT_LABEL: Record<GquestParticipant["result"], string> = {
  WON: "Won",
  EXT_COMPLETED: "Extended",
  DNF: "Did not finish"
};

const minutes = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div>
    <span>{label} </span>
    <span>{value}</span>
  </div>
);

// Everything about one GQ beyond its table row: rewards, extended details,
// mob targets and who took part (fetched when the row is opened).
const GquestDetail = ({ row }: { row: GquestRow }) => {
  const { isMe } = useMyPlayer();
  const [players, setPlayers] = useState<GquestParticipant[] | null>(
    participantCache.get(row.id) ?? null
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    if (participantCache.has(row.id)) return undefined;
    const controller = new AbortController();
    setError(false);
    axios
      .get<{ players: GquestParticipant[] }>(
        `${process.env.REACT_APP_API}public/gquests/${row.id}/players`,
        { signal: controller.signal }
      )
      .then((response) => {
        participantCache.set(row.id, response.data.players);
        if (!controller.signal.aborted) setPlayers(response.data.players);
      })
      .catch((requestError) => {
        if (axios.isCancel(requestError) || controller.signal.aborted) return;
        console.error("Failed to fetch global quest players", requestError);
        setError(true);
      });
    return () => controller.abort();
  }, [row.id]);

  const { rewards, extended } = row;
  const mine = players?.find((player) => isMe(player.player));

  return (
    <div className="gquest-mobs-panel">
      <div className="gquest-detail-strip">
        <Stat label="Quest points" value={rewards.questPoints.toLocaleString()} />
        <Stat label="Gold" value={rewards.gold.toLocaleString()} />
        {rewards.practice !== null && <Stat label="Practice" value={rewards.practice.toLocaleString()} />}
        {rewards.training !== null && <Stat label="Training" value={rewards.training.toLocaleString()} />}
        {rewards.trivia !== null && <Stat label="Trivia" value={rewards.trivia.toLocaleString()} />}
        {extended?.seconds != null && <Stat label="Extended for" value={minutes(extended.seconds)} />}
        {extended?.kills != null && <Stat label="Extended kills" value={extended.kills.toLocaleString()} />}
        {extended?.completions != null && <Stat label="Extended completions" value={extended.completions.toLocaleString()} />}
      </div>

      <Row className="g-3">
        <Col md={6}>
          <div className="epic-lb-card-title">Mob targets</div>
          <div className="gquest-detail-scroll">
            <table className="gquest-mobs-table">
              <thead><tr><th>Mob</th><th>Required</th><th>Reported kills</th></tr></thead>
              <tbody>{row.mobs.map((mob) => <tr key={mob.name}><td>{mob.name}</td><td>{mob.amount}</td><td>{mob.timesKilled}</td></tr>)}</tbody>
            </table>
          </div>
        </Col>
        <Col md={6}>
          <div className="epic-lb-card-title">Players{players ? ` (${players.length})` : ""}</div>
          {mine && (
            <div className="gquest-detail-mine">
              ★ You: {RESULT_LABEL[mine.result]} · {mine.mobsLeftFinal} mobs left
            </div>
          )}
          {error ? (
            <div className="epic-lb-muted">Couldn&apos;t load the players.</div>
          ) : !players ? (
            <Spinner animation="border" size="sm" variant="light" />
          ) : (
            <div className="gquest-detail-scroll">
              <table className="gquest-parts-table">
                <thead><tr><th>Player</th><th>Lvl</th><th>Result</th><th>Mobs left</th></tr></thead>
                <tbody>
                  {players.map((player) => (
                    <tr key={player.player}>
                      <td><PlayerLink player={player.player} tab="gquests" className="epic-lb-player-link" /></td>
                      <td>{player.level ?? "—"}</td>
                      <td>
                        <span className={`player-gquest-result player-gquest-result--${player.result.toLowerCase()}`}>
                          {RESULT_LABEL[player.result]}
                        </span>
                      </td>
                      <td>{player.mobsLeftFinal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default GquestDetail;
